import { useEffect, useRef, useState } from "react";
import { X, Copy, Share2 } from "lucide-react";
import { toast } from "sonner";

// Shareable WIN popup — fires on the global "vts:win" event from any engine.
export const WinPopup = () => {
  const [win, setWin] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    const onWin = (e) => {
      setWin({ ...e.detail, at: Date.now() });
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setWin(null), 15000);
    };
    window.addEventListener("vts:win", onWin);
    return () => { window.removeEventListener("vts:win", onWin); if (timerRef.current) clearTimeout(timerRef.current); };
  }, []);

  if (!win) return null;
  const link = window.location.origin;
  const text = `🎉 Just won +${win.amount.toFixed(2)} ${win.currency} on ${win.engine} at VTS Trading!${win.extra ? ` (${win.extra})` : ""} Free $10k demo — trade with me 👉 ${link}`;

  return (
    <div data-testid="win-popup" className="fixed inset-0 z-[90] flex items-center justify-center p-4" style={{ background: "rgba(3,7,15,0.72)", backdropFilter: "blur(4px)" }}>
      <div className="relative w-full max-w-sm rounded-2xl border border-vts-profit/40 bg-gradient-to-b from-[#07240f] via-[#0b1220] to-[#05080f] p-6 text-center space-y-3 shadow-2xl animate-in zoom-in-95 duration-300">
        <button data-testid="win-popup-close" onClick={() => setWin(null)}
          className="absolute top-3 right-3 text-vts-silver/50 hover:text-white"><X className="h-4 w-4" /></button>
        <div className="text-4xl">🎉</div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-vts-profit font-bold">Winner!</div>
        <div data-testid="win-popup-amount" className="font-mono text-4xl font-bold text-vts-profit tabular-nums">
          +{win.amount.toFixed(2)} <span className="text-lg">{win.currency}</span>
        </div>
        <div className="text-sm text-white font-semibold">{win.engine}</div>
        {win.extra && <div className="text-[11px] text-vts-silver/60 font-mono">{win.extra}</div>}
        {win.contract && (
          <div data-testid="win-popup-contract" className="text-[9px] text-vts-silver/45 font-mono">
            📜 Contract #{win.contract}{win.symbol ? ` · ${win.symbol}` : ""} — verifiable on Deriv
          </div>
        )}
        {win.note && (
          <div data-testid="win-popup-note" className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[10px] text-amber-300 font-mono leading-snug">
            💡 {win.note}
          </div>
        )}
        <div className="pt-1 space-y-2">
          <div className="text-[9px] uppercase tracking-wider text-vts-silver/50 font-mono">Share your win — it's real, let them see it</div>
          <div className="grid grid-cols-3 gap-2">
            <a data-testid="win-share-whatsapp" href={`https://wa.me/?text=${encodeURIComponent(text)}`} target="_blank" rel="noreferrer"
              className="rounded-lg bg-[#25D366]/15 border border-[#25D366]/40 py-2 text-[11px] font-bold text-[#4ce082] hover:bg-[#25D366]/25">
              WhatsApp
            </a>
            <a data-testid="win-share-telegram" href={`https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(text)}`} target="_blank" rel="noreferrer"
              className="rounded-lg bg-sky-500/15 border border-sky-500/40 py-2 text-[11px] font-bold text-sky-300 hover:bg-sky-500/25">
              Telegram
            </a>
            <button data-testid="win-share-copy"
              onClick={() => { navigator.clipboard?.writeText(text).then(() => toast.success("Copied — paste it anywhere!")).catch(() => {}); }}
              className="rounded-lg bg-vts-surface border border-vts-line py-2 text-[11px] font-bold text-vts-silver/80 hover:text-white flex items-center justify-center gap-1">
              <Copy className="h-3 w-3" /> Copy
            </button>
          </div>
          {navigator.share && (
            <button data-testid="win-share-native"
              onClick={() => navigator.share({ text }).catch(() => {})}
              className="w-full rounded-lg bg-vts-blue py-2 text-[11px] font-bold text-white hover:bg-vts-blue/85 flex items-center justify-center gap-1.5">
              <Share2 className="h-3.5 w-3.5" /> More apps…
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default WinPopup;
